import { Link } from 'react-router-dom';
import { projects, projectsById } from '../content/projects';
import MediaPlaceholder from './MediaPlaceholder';
import LullabaiCaseStudy from './LullabaiCaseStudy';
import Mf1CaseStudy from './Mf1CaseStudy';
import RemoteStudioCaseStudy from './RemoteStudioCaseStudy';
import { mediaAsset } from '../config/media';

export function HomePage() {
  return (
    <main id="main-content">
      <section className="portfolio-hero">
        <p className="portfolio-eyebrow">Product Engineering Leader · Web · Mobile · Media Technology</p>
        <h1>Complex media products, from discovery through launch.</h1>
        <div className="portfolio-hero__intro">
          <p className="portfolio-hero__lede">
            I lead cross-functional development across web, iOS, and tvOS, combining product strategy, UX design, technical planning, and hands-on engineering.
          </p>
          <div className="portfolio-actions">
            <Link className="portfolio-button" to="/work">View selected work</Link>
            <Link className="portfolio-text-link" to="/resume">View experience</Link>
          </div>
        </div>
        <img
          className="portfolio-hero__media"
          src={mediaAsset('images/home/charles-live-production.jpeg')}
          alt="Charles Van Alst Danner working at a live media production"
        />
      </section>

      <section className="portfolio-section" aria-labelledby="selected-work-heading">
        <div className="portfolio-section__heading">
          <p className="portfolio-eyebrow">Selected work</p>
          <h2 id="selected-work-heading">Product, interface, and systems work</h2>
        </div>
        <ProjectGrid />
      </section>

      <section className="portfolio-feature">
        <div>
          <p className="portfolio-eyebrow">Interactive installation</p>
          <h2>ASCII-TV</h2>
          <p>
            An audiovisual playground combining generative media, sound analysis, MIDI, video, and experimental interfaces.
          </p>
        </div>
        <Link className="portfolio-button portfolio-button--inverse" to="/ascii-tv">Enter the installation</Link>
      </section>
    </main>
  );
}

function ProjectGrid() {
  return (
    <div className="portfolio-grid">
      {projects.map((project, index) => (
        <article className="portfolio-card" key={project.path}>
          {project.media.cardImage ? (
            <img
              className={`portfolio-card__image portfolio-card__image--${project.id}`}
              src={project.media.cardImage}
              alt={project.media.cardImageAlt}
              loading="lazy"
            />
          ) : (
            <MediaPlaceholder label={project.media.card} ratio="4 / 3" tone={index === 1 ? 'accent' : 'light'} />
          )}
          <p className="portfolio-card__number">0{index + 1}</p>
          <p className="portfolio-card__type">{project.type}</p>
          <h3>{project.title}</h3>
          <p>{project.summary}</p>
          <Link to={project.path}>Read the case study <span aria-hidden="true">→</span></Link>
        </article>
      ))}
    </div>
  );
}

export function WorkIndexPage() {
  return (
    <main id="main-content" className="portfolio-page">
      <PageIntro eyebrow="Work" title="Selected professional work">
        Products and systems spanning real-time collaboration, consumer iOS, frontend engineering, automation, and hardware prototyping.
      </PageIntro>
      <ProjectGrid />
    </main>
  );
}

export function ProjectPage({ projectId }) {
  if (projectId === 'lullabai') {
    return <LullabaiCaseStudy />;
  }

  if (projectId === 'remote-studio') {
    return <RemoteStudioCaseStudy />;
  }

  if (projectId === 'mf-1') {
    return <Mf1CaseStudy />;
  }

  const project = projectsById[projectId];

  return (
    <main id="main-content" className="portfolio-page">
      <PageIntro eyebrow={project.type} title={project.title}>
        {project.summary}
      </PageIntro>
      <section className="case-study-summary" aria-label={`${project.title} project summary`}>
        <div>
          <p className="portfolio-eyebrow">Role</p>
          <p>{project.role}</p>
        </div>
        <div>
          <p className="portfolio-eyebrow">Period</p>
          <p>{project.period}</p>
        </div>
      </section>
      <MediaPlaceholder className="case-study-hero" label={project.media.hero} ratio="16 / 9" tone="dark" />
      <section className="case-study-layout" aria-labelledby="case-study-status">
        <div className="portfolio-notice case-study-highlights">
          <h2 id="case-study-status">Selected contributions</h2>
          <ul>
            {project.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
          </ul>
        </div>
        <MediaPlaceholder label={project.media.process} ratio="4 / 3" />
        <MediaPlaceholder label={project.media.detail} ratio="4 / 3" tone="accent" />
        <div className="case-study-next">
          <p className="portfolio-eyebrow">Navigation</p>
          <Link to="/work">Return to selected work <span aria-hidden="true">→</span></Link>
        </div>
      </section>
    </main>
  );
}

export function ArtPage() {
  return (
    <main id="main-content" className="portfolio-page">
      <PageIntro eyebrow="Art" title="Selected artistic work">
        Performances, sound work, installations, and emerging-media pieces presented with their medium, context, year, and collaborators.
      </PageIntro>
      <section className="artwork-feature" aria-labelledby="tip-scales-heading">
        <div className="artwork-feature__details">
          <p className="portfolio-eyebrow">Kinetic sound sculpture · CalArts WaveCave · April 2019</p>
          <h2 id="tip-scales-heading"><cite>To Tip The Scales of Justice</cite></h2>
          <p className="artwork-feature__summary">
            <cite>To Tip The Scales of Justice</cite> is a kinetic sound sculpture about the way media systems flatten complex social and political ideas into simple choices. Visitors place coins inside suspended sculptural heads, changing both the balance of the sculpture and the sound it produces. Each head holds a different interpretation of justice. The pull of gravity reflects the commercial pressure behind what information gets selected and circulated.
          </p>
          <p className="artwork-feature__summary">
            The sound combines recordings of speeches, lectures, and conversations with computer-generated voices. Tape loops alter their speed, pitch, and fidelity as the sculpture moves. The resulting distortion makes the loss of information audible and shows how repetition can reinforce a particular point of view.
          </p>
          <a
            className="portfolio-text-link"
            href="https://www.youtube.com/watch?v=PwuzOdRg_3s"
            target="_blank"
            rel="noreferrer"
          >
            Watch on YouTube <span aria-hidden="true">↗</span>
          </a>
        </div>
        <div className="artwork-feature__video">
          <iframe
            src="https://www.youtube-nocookie.com/embed/PwuzOdRg_3s"
            title="To Tip The Scales of Justice"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </section>
      <section className="artwork-feature artwork-feature--bodyjolt" aria-labelledby="bodyjolt-heading">
        <div className="artwork-feature__details">
          <p className="portfolio-eyebrow">Theater · Music composition + production · 2019</p>
          <h2 id="bodyjolt-heading"><cite>Walt Whitman BodyJolt™</cite></h2>
          <p className="artwork-feature__role">Music composed and produced by Van Broh, the electronic music duo of Charles Van Alst Danner and Ben Brodie</p>
          <p className="artwork-feature__summary">
            <cite>Walt Whitman BodyJolt™</cite> was an audience participation piece staged as an exercise class led by Walt Whitman. It drew on Whitman’s writing about health and fitness, asking the audience to join a physical, intellectual, and moral workout. Along the way, the characters confronted their own obstacles and insecurities.
          </p>
          <p className="artwork-feature__summary">
            Ben Brodie and I composed and produced the music as Van Broh. Alex Hare wrote and directed the piece using Whitman’s words, with choreography by Katie Roe McLaughlin and Natalie Michelle Hratko. The production was presented at the Paradise Factory as part of Corkscrew Downstairs during the 2019 Corkscrew Theater Festival.
          </p>
          <div className="artwork-feature__credits" aria-label="Selected production credits">
            <p><strong>Written + directed</strong><span>Alex Hare</span></p>
            <p><strong>Music</strong><span>Van Broh</span></p>
            <p><strong>Choreography</strong><span>Katie Roe McLaughlin + Natalie Michelle Hratko</span></p>
            <p><strong>Costume design</strong><span>Karen Boyer</span></p>
          </div>
          <div className="portfolio-actions">
            <a className="portfolio-text-link" href="https://playbill.com/article/check-out-the-lineup-for-the-2019-corkscrew-theater-festival" target="_blank" rel="noreferrer">Playbill <span aria-hidden="true">↗</span></a>
            <a className="portfolio-text-link" href="https://www.broadwayworld.com/off-off-broadway/article/Corkscrew-Theater-Festival-Launches-Workshop-Series-CORKSCREW-DOWNSTAIRS-20190620" target="_blank" rel="noreferrer">BroadwayWorld <span aria-hidden="true">↗</span></a>
          </div>
        </div>
        <div className="artwork-feature__gallery">
          <img
            src={mediaAsset('images/art/bodyjolt-production.jpg')}
            alt="Walt Whitman BodyJolt performers leading an audience-participatory exercise sequence onstage"
            loading="lazy"
          />
          <img
            src={mediaAsset('images/art/bodyjolt-performance.jpg')}
            alt="A performer in the Walt Whitman BodyJolt production"
            loading="lazy"
          />
        </div>
      </section>
      <section className="artwork-feature artwork-feature--lazer" aria-labelledby="lazer-eyes-heading">
        <div className="artwork-feature__details">
          <p className="portfolio-eyebrow">Electronic dance opera · Composition + production · 2019</p>
          <h2 id="lazer-eyes-heading"><cite>Lazer Eyes</cite></h2>
          <p className="artwork-feature__role">Co-creator, composer, and producer as one half of Van Broh</p>
          <p className="artwork-feature__summary">
            <cite>Lazer Eyes</cite> began as a concept album that Ben Brodie and I wrote, composed, and produced as Van Broh. Amanda + James later adapted the album into an evening-length electronic dance opera with live performance, dance, video, and narration.
          </p>
          <p className="artwork-feature__summary">
            The story follows a young Appalachian miner who is transformed by a meteor into Lazer Eyes. He sets out to find his true love, Leila, and confronts the Reflektor, a smart device trying to control Cityopolis. The plot mixes heroic mythology with millennial anti-humor and a satire of life mediated through screens.
          </p>
          <p className="artwork-feature__summary">
            We used the album to see how far the production language and distinct subgenres of EDM could carry a full operatic narrative. The stage version premiered at National Sawdust in Brooklyn on February 1, 2019.
          </p>
          <div className="artwork-feature__credits" aria-label="Selected Lazer Eyes credits">
            <p><strong>Written + composed</strong><span>Van Broh</span></p>
            <p><strong>Produced + adapted</strong><span>Amanda + James</span></p>
            <p><strong>Direction + choreography</strong><span>Amanda Hameline in collaboration with the dancers</span></p>
            <p><strong>Video design</strong><span>Nicholas DiMichele</span></p>
          </div>
          <div className="portfolio-actions">
            <a className="portfolio-button" href="https://soundcloud.com/lazersforeyes/sets/lazer-eyes-1" target="_blank" rel="noreferrer">Listen on SoundCloud</a>
            <a className="portfolio-text-link" href="https://amandaplusjames.com/project/lazereyes/" target="_blank" rel="noreferrer">Project page <span aria-hidden="true">↗</span></a>
            <a className="portfolio-text-link" href="https://vimeo.com/259091617" target="_blank" rel="noreferrer">Watch the trailer <span aria-hidden="true">↗</span></a>
          </div>
        </div>
        <div className="artwork-feature__gallery artwork-feature__gallery--lazer">
          <img
            src={mediaAsset('images/art/lazer-eyes-performance.webp')}
            alt="Lazer Eyes and the Reflektor performing in the electronic dance opera"
            loading="lazy"
          />
          <img
            src={mediaAsset('images/art/lazer-eyes-album.gif')}
            alt="Animated neon Lazer Eyes album artwork by Van Broh"
            loading="lazy"
          />
        </div>
      </section>
    </main>
  );
}

export function LabPage() {
  return (
    <main id="main-content" className="portfolio-page">
      <PageIntro eyebrow="Lab" title="Experiments and concepts">
        Prototypes, technical sketches, and unfinished questions documented honestly through their status, methods, and lessons.
      </PageIntro>
      <section className="lab-feature" aria-labelledby="news-pipeline-lab-heading">
        <div className="lab-feature__signal" aria-hidden="true">
          <span>RSS</span><b>→</b><span>LLM</span><b>→</b><span>TTS</span><b>→</b><span>FFMPEG</span>
        </div>
        <div className="lab-feature__copy">
          <p className="portfolio-eyebrow">Autonomous media systems · Active experiment</p>
          <h2 id="news-pipeline-lab-heading">Fake News</h2>
          <p>An autonomous, multi-agent synthetic news broadcast system built around editorial orchestration rather than a single generative model.</p>
          <div className="lab-feature__metrics" aria-label="Project benchmarks">
            <span><strong>0.594×</strong> radio render factor</span>
            <span><strong>12</strong> stories per benchmark</span>
            <span><strong>6</strong> independent sources</span>
          </div>
          <Link className="portfolio-button" to="/lab/news-pipeline">Read the engineering case study</Link>
        </div>
      </section>
      <section className="lab-feature" aria-labelledby="ascii-tv-lab-heading">
        <div className="lab-feature__signal" aria-hidden="true">
          <span>ARCHIVE</span><b>→</b><span>VIDEO</span><b>→</b><span>ASCII</span><b>→</b><span>CRT</span>
        </div>
        <div className="lab-feature__copy">
          <p className="portfolio-eyebrow">Browser installation · Interactive time capsule</p>
          <h2 id="ascii-tv-lab-heading">ASCII-TV</h2>
          <p>A browser-based time capsule that transforms period broadcasts into ASCII, making the conversion from analog signal to binary data visible—and asking how digitization changes the way media is made, consumed, and experienced.</p>
          <Link className="portfolio-button" to="/ascii-tv">Enter the installation</Link>
        </div>
      </section>
    </main>
  );
}

export function AboutPage() {
  return (
    <main id="main-content" className="portfolio-page">
      <PageIntro eyebrow="About" title="Between engineering and artistic practice">
        I am a product and engineering leader with more than ten years of experience building web, mobile, and real-time media products in startup and nonprofit environments.
      </PageIntro>
      <img
        className="about-portrait"
        src={mediaAsset('images/upload-34336711-2323-464d-a4b8-0777614a3370.webp')}
        alt="Charles Van Alst Danner holding a dog while both wear flower crowns"
      />
      <section className="portfolio-prose">
        <p>
          I currently lead web and mobile product engineering at Remoto, directing development across web, iPhone, iPad, and Apple TV while translating customer and business needs into product requirements, prototypes, technical specifications, roadmaps, and release plans.
        </p>
        <p>
          My broader practice connects product strategy and frontend engineering with music technology, real-time audio and video, UX design, embedded systems, and fabrication. I am one half of Van Broh, an electronic-music duo with Ben Brodie, and co-founded Lullabai, an iOS mindfulness product built around continuously evolving music. I also serve as Technical Director for the arts nonprofit Amanda + James.
        </p>
        <p>
          I hold an MFA in Music Technology: Interaction, Intelligence & Design from California Institute of the Arts and a BM in Music Technology from New York University.
        </p>
      </section>
      <section className="resume-section" aria-labelledby="selected-research-heading">
        <p className="portfolio-eyebrow">Selected research</p>
        <h2 id="selected-research-heading">Autonomous systems as artistic tools</h2>
        <div className="portfolio-prose">
          <h3><cite>smArt: An Analytical Framework for Artistic Applications of Autonomous and Semi-Autonomous Systems</cite></h3>
          <p className="resume-section__meta">MFA thesis · CalArts MTIID · 2020</p>
          <p>
            An examination of how autonomous and semi-autonomous systems can be evaluated as artistic tools, with attention to authorship, agency, interaction, and creative intent. The research provides a foundation for my later work in generative music, interactive systems, and human-machine collaboration.
          </p>
          <div className="portfolio-actions">
            <a className="portfolio-button" href="https://mtiid.calarts.edu/wp-content/uploads/2020/06/MTEC690_Thesis_Danner3.1.pdf" target="_blank" rel="noreferrer">Read the thesis</a>
            <a className="portfolio-text-link" href="https://mtiid.calarts.edu/theses/" target="_blank" rel="noreferrer">View the MTIID archive</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export function ResumePage() {
  return (
    <main id="main-content" className="portfolio-page">
      <PageIntro eyebrow="Resume" title="Product engineering leadership across web, mobile, and media">
        More than ten years of experience taking complex products from customer discovery through launch and iteration.
      </PageIntro>
      <section className="resume-skills resume-skills--summary" aria-labelledby="capabilities-heading">
        <p className="portfolio-eyebrow">At a glance</p>
        <h2 id="capabilities-heading">Core capabilities</h2>
        <div className="resume-skills__grid">
          <div><h3>Product & Leadership</h3><p>Strategy, roadmaps, requirements, research, prototyping, Agile/Scrum, and cross-functional leadership.</p></div>
          <div><h3>Web, Mobile & Cloud</h3><p>React, React Native, JavaScript, TypeScript, HTML/CSS, iOS, tvOS, AWS, REST APIs, Git, and CI/CD.</p></div>
          <div><h3>Media & Real-Time Systems</h3><p>C++, Python, Qt/QML, JUCE, GStreamer, DSP, WebRTC, SRT, Dante, and AES67.</p></div>
          <div><h3>Design & Prototyping</h3><p>Figma, Adobe Creative Suite, TouchDesigner, embedded systems, Arduino, electronics, and fabrication.</p></div>
        </div>
      </section>
      <section className="resume-section" aria-labelledby="professional-experience-heading">
        <p className="portfolio-eyebrow">Professional experience</p>
        <h2 id="professional-experience-heading">Leadership and product engineering</h2>
        <ResumeEntry
          title="Head of Web & Mobile Product Engineering"
          organization="Remoto"
          location="Burbank, CA"
          period="2020–Present"
          items={[
            'Lead a two-person product engineering team that delivered 46 major product features across six web, mobile, and Apple TV applications from July 2025 through July 2026.',
            'Built an AI-powered, computer-vision UI automation harness covering 79 onboarding scenarios across three applications and reducing regression testing from approximately 70 hours to 30 minutes per release.',
            'Direct development of 4K, multichannel remote-collaboration clients across web, iPhone, iPad, and Apple TV.',
            'Translate customer and business needs into requirements, prototypes, technical specifications, prioritized roadmaps, and release plans.',
            'Built and launched customer-facing e-commerce, account-management, and public website experiences.',
            'Delivered administration, auditing, analytics, and user-metrics platforms supporting internal operations and customer management.',
            'Partner directly with the CEO and CTO across discovery, development, launch, and iteration.',
          ]}
        />
      </section>

      <section className="resume-section" aria-labelledby="entrepreneurial-experience-heading">
        <p className="portfolio-eyebrow">Entrepreneurial & consulting experience</p>
        <h2 id="entrepreneurial-experience-heading">Products, organizations, and creative systems</h2>
        <ResumeEntry
          title="Founder & Product Engineer"
          organization="Lullabai LLC"
          location="New York, NY"
          period="2020–Present"
          items={[
            'Founded and launched an iOS mindfulness application that generates continuously evolving music for sleep, meditation, and relaxation.',
            'Designed and developed the consumer iOS experience through release on the U.S. App Store.',
            'Created an original music-composition dataset and data-collection methodology for the platform’s generative system.',
            'Built sound-design, music-production, and audio-content pipelines supporting multiple musical styles.',
          ]}
        />
        <ResumeEntry
          title="Technical Director"
          organization="Amanda + James"
          location="New York, NY"
          period="2014–Present"
          items={[
            'Provide technical direction, website management, production consultation, and audio engineering for a 501(c)(3) arts nonprofit.',
            'Support artist projects through multitrack recording, mixing, mastering, equipment integration, and on-site troubleshooting.',
            'Lead technical planning for the Relay Room, a multimedia rehearsal and artist-residency space developed with 3AM Theatre.',
          ]}
        />
        <ResumeEntry
          title="Sound Designer"
          organization="Brooklyn Art Haus"
          location="Brooklyn, NY"
          period="2022–2024"
          items={[
            'Designed and installed audio systems for a 100-person black box theater, two rehearsal studios, gallery, café, and podcasting studio.',
            'Implemented a facility-wide Dante/AES67 network enabling interconnected recording, routing, and playback across the venue.',
          ]}
        />
      </section>

      <section className="resume-section" aria-labelledby="education-heading">
        <p className="portfolio-eyebrow">Education</p>
        <h2 id="education-heading">Music technology and interaction design</h2>
        <div className="resume-education">
          <article>
            <h3>California Institute of the Arts, Herb Alpert School of Music</h3>
            <p>MFA in Music Technology: Interaction, Intelligence & Design · 2020</p>
            <p><a href="https://mtiid.calarts.edu/wp-content/uploads/2020/06/MTEC690_Thesis_Danner3.1.pdf" target="_blank" rel="noreferrer"><cite>smArt: An Analytical Framework for Artistic Applications of Autonomous and Semi-Autonomous Systems</cite></a></p>
            <p className="resume-section__meta">Valencia, CA</p>
          </article>
          <article>
            <h3>New York University, Steinhardt School of Culture, Education, and Human Development</h3>
            <p>BM in Music Technology · 2016</p>
            <p className="resume-section__meta">New York, NY</p>
          </article>
        </div>
      </section>

      <section className="resume-section" aria-labelledby="additional-experience-heading">
        <p className="portfolio-eyebrow">Additional experience</p>
        <h2 id="additional-experience-heading">Project development</h2>
        <ResumeEntry
          title="Participant"
          organization="CalArts Walt Disney Imagineering Education Initiative"
          location="Burbank, CA"
          period="2020"
          items={[
            'Selected from across the CalArts student body for a project-development initiative working with Disney Imagineers.',
          ]}
        />
      </section>

      <div className="portfolio-actions">
        <Link className="portfolio-button" to="/contact">Contact Charles</Link>
        <Link className="portfolio-text-link" to="/work">Review selected work</Link>
      </div>
    </main>
  );
}

export function ContactPage() {
  return (
    <main id="main-content" className="portfolio-page">
      <PageIntro eyebrow="Contact" title="Start a conversation">
        For product engineering leadership, frontend, mobile, real-time media, and creative-technology opportunities, contact me directly.
      </PageIntro>
      <div className="contact-links">
        <a className="portfolio-button" href="mailto:cvadbooking@gmail.com">Email Charles</a>
        <a className="portfolio-text-link" href="https://www.linkedin.com/in/charles-danner-2953a2a2/" target="_blank" rel="noreferrer">LinkedIn</a>
      </div>
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main id="main-content" className="portfolio-page">
      <PageIntro eyebrow="404" title="This signal is unavailable.">
        The requested route does not exist or has moved.
      </PageIntro>
      <Link className="portfolio-button" to="/">Return home</Link>
    </main>
  );
}

function PageIntro({ eyebrow, title, children }) {
  return (
    <header className="portfolio-page-intro">
      <p className="portfolio-eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{children}</p>
    </header>
  );
}

function ResumeEntry({ title, organization, location, period, items }) {
  return (
    <article className="resume-entry">
      <header>
        <h3>{title}, {organization}</h3>
        <p className="resume-section__meta">{location} · {period}</p>
      </header>
      <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </article>
  );
}

function EmptyCollection({ title }) {
  return (
    <section className="portfolio-notice">
      <h2>{title}</h2>
      <p>Entries will be added only when their documentation and status accurately represent the work.</p>
    </section>
  );
}

function CollectionPlaceholders({ type }) {
  return (
    <div className="portfolio-grid portfolio-grid--collection" aria-label={`${type} image placeholders`}>
      {[1, 2, 3].map((number) => (
        <MediaPlaceholder
          key={number}
          label={`${type} ${number}`}
          ratio={number === 2 ? '4 / 5' : '4 / 3'}
          tone={number === 2 ? 'dark' : 'light'}
        />
      ))}
    </div>
  );
}
